import { useEffect, useMemo, useState } from "react";
import { feMonthly, recordingCoverage } from "@/lib/api";
import { ChevronLeft, ChevronRight, Users, ShoppingCart, TrendingUp, Play, IndianRupee, Percent, Mic2 } from "lucide-react";
import { numberLocale } from "@/lib/money";

const fmtINR = (n) => new Intl.NumberFormat(numberLocale(), { maximumFractionDigits: 0 }).format(n || 0);
const pct = (n) => `${((n || 0) * 100).toFixed(n && n < 1 ? 1 : 0)}%`;
const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

export default function FEMonthly() {
  const now = useMemo(() => new Date(), []);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);

  useEffect(() => { feMonthly(year, month).then(setData); }, [year, month]);
  const shift = (n) => {
    let y = year, m = month + n;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setYear(y); setMonth(m);
  };

  return (
    <div className="space-y-8" data-testid="fe-monthly-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">New Consult · Calendar month</div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Monthly Sales Dashboard</h2>
          <p className="text-sm text-zinc-400 mt-1">Consults, conversion, revenue collected and rep incentives for the selected month. Incentives count each sale in the <span className="text-zinc-300">month its programme started</span> (the nutrition consult was done); a later balance or refund counts in the month it happens.</p>
        </div>
        <div className="inline-flex items-center gap-1 rounded-full border border-[#262626] bg-[#0a0a0a] px-1 py-1" data-testid="fe-month-picker">
          <button onClick={() => shift(-1)} className="p-1.5 rounded-full hover:bg-[#1a1a1a]" data-testid="fe-prev-month" aria-label="Previous month"><ChevronLeft className="h-4 w-4 text-zinc-400" /></button>
          <div className="text-sm text-zinc-100 min-w-[7rem] text-center font-medium" data-testid="fe-month-label">{MONTH_NAMES[month - 1]} {year}</div>
          <button onClick={() => shift(1)} className="p-1.5 rounded-full hover:bg-[#1a1a1a]" data-testid="fe-next-month" aria-label="Next month"><ChevronRight className="h-4 w-4 text-zinc-400" /></button>
        </div>
      </div>

      {!data ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : (
        <>
          <section>
            <div className="eyebrow mb-3">Sales activity</div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <Stat label="Consults" value={data.consults} icon={Users} testid="fe-consults" />
              <Stat label="Sold" value={data.sold} icon={ShoppingCart} tint="emerald" testid="fe-sold" />
              <Stat label="Conversion" value={pct(data.conversion)} icon={Percent} tint={data.conversion >= 0.3 ? "emerald" : data.conversion >= 0.15 ? "amber" : "rose"} testid="fe-conversion" />
              <Stat label="Programmes started" value={data.programmes_started} icon={Play} tint="violet" testid="fe-started" />
              <Stat label="Revenue collected" value={`${window.__ACTIVE_CURRENCY_SYMBOL__ || ""}${fmtINR(data.revenue_collected)}`} icon={IndianRupee} tint="blue" testid="fe-revenue" />
              <Stat label="Incentive payout" value={`${window.__ACTIVE_CURRENCY_SYMBOL__ || ""}${fmtINR(data.incentive_this_month)}`} icon={TrendingUp} tint="amber" testid="fe-incentive" />
            </div>
          </section>

          <RecordingCoverageSection year={year} month={month} />

          <section className="card-surface p-6">
            <div className="eyebrow">Rep breakdown</div>
            <h3 className="font-display text-xl font-bold mt-1 mb-4">Sales Executives — {MONTH_NAMES[month - 1]} {year}</h3>
            {data.reps.length === 0 ? (
              <div className="text-sm text-zinc-500 py-8 text-center">No activity yet for this month.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="fe-reps-table">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-[0.2em] text-zinc-500 border-b border-[#1f1f1f]">
                      <th className="px-3 py-2 font-medium">Rep</th>
                      <th className="px-3 py-2 font-medium text-right">Consults</th>
                      <th className="px-3 py-2 font-medium text-right">Sold</th>
                      <th className="px-3 py-2 font-medium text-right">Conversion</th>
                      <th className="px-3 py-2 font-medium text-right">Programmes started</th>
                      <th className="px-3 py-2 font-medium text-right">Incentive base</th>
                      <th className="px-3 py-2 font-medium text-right">Slab</th>
                      <th className="px-3 py-2 font-medium text-right">Incentive</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.reps || []).map((r) => (
                      <tr key={r.name} className="border-b border-[#1a1a1a] last:border-0 hover:bg-[#161616]" data-testid={`fe-rep-${r.name}`}>
                        <td className="px-3 py-2.5 font-medium">{r.name}</td>
                        <td className="px-3 py-2.5 text-right text-zinc-300 tabular-nums">{r.consults}</td>
                        <td className="px-3 py-2.5 text-right text-zinc-300 tabular-nums">{r.sold}</td>
                        <td className="px-3 py-2.5 text-right"><span className={`text-xs font-mono ${r.conversion >= 0.4 ? "text-emerald-400" : r.conversion >= 0.2 ? "text-amber-400" : "text-rose-400"}`}>{pct(r.conversion)}</span></td>
                        <td className="px-3 py-2.5 text-right text-violet-300 tabular-nums">{r.programmes_started}</td>
                        <td className="px-3 py-2.5 text-right font-mono text-emerald-300">{window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(r.incentive_base)}</td>
                        <td className="px-3 py-2.5 text-right font-mono text-zinc-400">{pct(r.incentive_rate)}</td>
                        <td className="px-3 py-2.5 text-right font-mono text-amber-300 font-semibold">{window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{fmtINR(r.incentive_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, icon: Icon, tint, testid }) {
  const tints = {
    emerald: "text-emerald-300",
    amber: "text-amber-300",
    rose: "text-rose-300",
    blue: "text-blue-300",
    violet: "text-violet-300",
  };
  return (
    <div className="card-surface p-4" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">{label}</div>
        {Icon && <Icon className={`h-3.5 w-3.5 ${tints[tint] || "text-zinc-500"}`} />}
      </div>
      <div className={`font-display text-2xl font-bold tabular-nums mt-1.5 ${tints[tint] || "text-zinc-100"}`}>{value}</div>
    </div>
  );
}

function RecordingCoverageSection({ year, month }) {
  const [data, setData] = useState({ overall: { leads: 0, with_recording: 0, coverage_pct: 0 }, by_rep: [] });
  useEffect(() => {
    recordingCoverage(year, month).then(setData).catch(() => {});
  }, [year, month]);
  const overall = data.overall || {};
  const tone = (p) => p >= 90 ? "emerald" : p >= 70 ? "amber" : "rose";
  const overallTone = tone(overall.coverage_pct || 0);
  return (
    <section data-testid="recording-coverage-section">
      <div className="eyebrow mb-3">Call recording coverage · this month</div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card-surface p-6 md:col-span-1" data-testid="kpi-recording-overall">
          <div className="flex items-center justify-between">
            <div className="eyebrow">Overall coverage</div>
            <Mic2 className={`h-5 w-5 ${overallTone === "emerald" ? "text-emerald-400" : overallTone === "amber" ? "text-amber-400" : "text-rose-400"}`} strokeWidth={1.5} />
          </div>
          <div className={`font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold mt-3 tabular-nums ${overallTone === "emerald" ? "text-emerald-400" : overallTone === "amber" ? "text-amber-400" : "text-rose-400"}`}>
            {overall.coverage_pct || 0}%
          </div>
          <div className="text-xs text-zinc-500 mt-2">
            {overall.with_recording || 0} of {overall.leads || 0} leads have a recording
          </div>
        </div>
        <div className="md:col-span-3 card-surface p-4" data-testid="recording-coverage-table">
          {(data.by_rep || []).length === 0 ? (
            <div className="text-sm text-zinc-500 py-8 text-center">No leads logged this month yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 border-b border-[#1f1f1f]">
                    <th className="px-3 py-2 font-medium">Rep</th>
                    <th className="px-3 py-2 font-medium text-right">Leads</th>
                    <th className="px-3 py-2 font-medium text-right">With recording</th>
                    <th className="px-3 py-2 font-medium text-right">Coverage %</th>
                    <th className="px-3 py-2 font-medium">Bar</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.by_rep || []).map((r) => {
                    const t = tone(r.coverage_pct || 0);
                    const barCls = t === "emerald" ? "bg-emerald-400" : t === "amber" ? "bg-amber-400" : "bg-rose-400";
                    const pctCls = t === "emerald" ? "text-emerald-300" : t === "amber" ? "text-amber-300" : "text-rose-300";
                    return (
                      <tr key={r.rep} className="border-b border-[#1a1a1a] last:border-0 hover:bg-[#161616]" data-testid={`recording-cov-row-${r.rep}`}>
                        <td className="px-3 py-2.5 font-medium">{r.rep}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-zinc-300">{r.leads}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-zinc-300">{r.with_recording}</td>
                        <td className={`px-3 py-2.5 text-right font-mono font-semibold ${pctCls}`}>{r.coverage_pct}%</td>
                        <td className="px-3 py-2.5 w-40">
                          <div className="h-2 rounded-full bg-[#1a1a1a] overflow-hidden">
                            <div className={`h-2 ${barCls}`} style={{ width: `${Math.max(3, r.coverage_pct)}%` }} />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
